// Tanner Fry
// dev.lasutriv@gmail.com
// Component used for creating a trask for the task tracker.

import '../../css/components/Animations.css';

import { ErrorMessage, Field, Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APIGetUserLoginResponse, ReturnPropsAPIGetUserLoginResponse } from '../../../api/APIUsers';
import { useAppDispatch, useAppSelector } from '../../../AppHooks';
import { SubscriptionPlan } from '../data/SubscriptionData';
import Loading from '../elements/Loading';
import { CustomPasswordField } from '../inputs/CommonInputs';
import { trackUserLogin } from '../../../utils/analytics';
import _settings, { _settings_non_private_routes } from '../../../AppSettings';
import { setLoggedIn, setSubscriptionStatus } from '../../../slices/UserSlice';

interface ILoginFormProps {
    userPlan: SubscriptionPlan;
    apiLoginData: ReturnPropsAPIGetUserLoginResponse;
    setApiLoginData: (apiLoginData: ReturnPropsAPIGetUserLoginResponse) => void;
}

function LoginForm(props: ILoginFormProps) {  
    const navigate = useNavigate();
    const location = useLocation();
    const [passedPasswordVerification, setPassedPasswordVerification] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isCheckingFormErrorPlacement, setIsCheckingFormErrorPlacement] = useState(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        setIsCheckingFormErrorPlacement(true);
    }, [])

    useEffect(() => {
        // Get form error element and change position
        let formErrorElements = document.getElementsByClassName('form-error');
        if (formErrorElements.length > 0) {
            // Loop
            for (let i = 0; i < formErrorElements.length; i++) {
                // Get form error element's width
                let formErrorElementWidth = formErrorElements[i].clientWidth;
                // Set form error element's left position
                let formErrorStyle = 'left: -' + (10 + formErrorElementWidth) + 'px;';
                // Set form error element's style
                formErrorElements[i].setAttribute('style', formErrorStyle);
            }
            // console.log("formErrorElements: ", formErrorElements);
            
        }

        setIsCheckingFormErrorPlacement(false);
    }, [isCheckingFormErrorPlacement])

    useEffect(() => {
        const incomingSnackbarMessage = location.state?.snackbarMessage;
        if (incomingSnackbarMessage) {
            setSnackbarMessage(incomingSnackbarMessage);
            navigate(location.pathname, { replace: true, state: {} });

            const snackbarTimer = setTimeout(() => {
                setSnackbarMessage('');
            }, 3500);

            return () => clearTimeout(snackbarTimer);
        }
    }, [location.pathname, location.state, navigate]);

    const lastLocationWhileLoggedIn = useAppSelector(state => state.users.lastLocation);

    // OTP is disabled - skip verification step
    useEffect(() => {
        // Skip OTP verification - go directly to dashboard after successful login
        if (passedPasswordVerification) {
            // Set logged in state
            if ((typeof JSON.parse(localStorage.getItem('user'))?.uuid) == 'string') {
                dispatch(setLoggedIn(true));
            }
            
            // Navigate to dashboard or last location
            if (lastLocationWhileLoggedIn === "" || lastLocationWhileLoggedIn === '/login' || lastLocationWhileLoggedIn === '/register') {
                navigate("/dashboard");
            } else if (!_settings_non_private_routes.includes(lastLocationWhileLoggedIn)) {
                navigate(lastLocationWhileLoggedIn);
            } else {
                navigate("/dashboard");
            }
        }
    }, [passedPasswordVerification, navigate, dispatch, lastLocationWhileLoggedIn]);

    async function submitForm(values, setSubmitting, setFieldValue) {
        // Submit results to db
        const apiLoginData: ReturnPropsAPIGetUserLoginResponse = await APIGetUserLoginResponse(values.email, values.pass, dispatch); 
        // console.log("apiLoginData: ", apiLoginData);
        if (apiLoginData.error === "") {

            // console.log("apiLoginData: ", apiLoginData);
            // return;
            
            // Set apiLoginData
            props.setApiLoginData(apiLoginData);

            // Track successful login attempt
            trackUserLogin();

            // Set subscription status
            dispatch(setSubscriptionStatus(apiLoginData.subscriberPlan ? apiLoginData.subscriberPlan : SubscriptionPlan.Free));
            setErrorMessage('');

            // If OTP is enabled for this user, route to OTP verification screen.
            if (apiLoginData.hasOTPSetup) {
                navigate('/login/verify');
                setSubmitting(false);
                return;
            }

            // No OTP setup, continue with direct login flow.
            setPassedPasswordVerification(true);
            setSubmitting(false);
        } else {
            setErrorMessage(apiLoginData.error);
            setFieldValue('pass', '');
            setTimeout(() => {
                const passwordInput = document.querySelector('input[name="pass"]') as HTMLInputElement | null;
                if (passwordInput) {
                    passwordInput.focus();
                }
            }, 0);
            setSubmitting(false);
        }
    }

    function resetForm() {
        
    }

    function handleRegisterClick() {
        navigate("/register");
    }

    function handleResetPasswordClick() {
        navigate("/pre-password-reset");
    }

    return (
        <>
            {/* <img className="login-splash-screen" src={require('../../files/splashScreens/Splash-Screen-Farmland.png')} alt="Login Splash Screen" /> */}
            <motion.div
                className='form-container-login-register'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
            >
                <Formik
                    initialValues={ { email: '', pass: '' } }
                    validate={ values => {
                        setIsCheckingFormErrorPlacement(true);
                        const errors = {
                            email: '',
                            pass: ''
                        };
                        
                        if (!values.email) {
                            errors.email = 'Required';
                        } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/i.test(values.email)) {
                            errors.email = 'Invalid email address';
                        }
                        if (!values.pass) {
                            errors.pass = 'Required'
                        }

                        if (values.email && /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/i.test(values.email) && values.pass) {
                            return {};
                        } else {
                            return errors;
                        }
                    }}
                    onSubmit={ (values, { setSubmitting, setFieldValue }) => {
                        submitForm(values, setSubmitting, setFieldValue);
                    }}
                    onReset={ (values) => {
                        resetForm();
                    }}
                    enableReinitialize={true}
                >
                    { ({ isSubmitting }) => (
                        <>
                            { isSubmitting ? (
                                <>
                                    <Loading withBackground={true} />
                                </>
                            ) : (
                                <Form>
                                    <h2>Login</h2>
                                    <div className='form-group-inputs'>
                                        <div className='field-group'>
                                            {/* <div className='form-field-title'>Email<div className='required-asterisk'>*</div></div> */}
                                            <Field type='email' name='email' placeholder='Email' as='input' autoFocus autoComplete='username' />
                                            <ErrorMessage name='email' component='div' className='form-error' />
                                        </div>
                                        <div className='field-group'>
                                            {/* <div className='form-field-title'>Password<div className='required-asterisk'>*</div></div> */}
                                            <Field as={CustomPasswordField} name='pass' placeholder='Password' autoComplete='new-password' />
                                            <ErrorMessage name='pass' component='div' className='form-error' />
                                        </div>
                                    </div>
                                    <hr />
                                    <div className='button-group'>
                                        <button id="cancel-btn" type='reset'>Cancel</button>
                                        <button id="login-btn" type='submit' disabled={ isSubmitting }>Login</button>
                                        {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                                            <button id="register-btn" type="button" onClick={handleRegisterClick}>Register</button>
                                        )}
                                        <button id="reset-btn" type="button" onClick={handleResetPasswordClick}>Reset Password</button>
                                    </div>
                                    <div className={`error-message ${(errorMessage !== '' && errorMessage !== null) ? 'with-background' : ''}`}>
                                        {(errorMessage !== '' && errorMessage !== null) ? errorMessage : ''}
                                    </div>
                                </Form>
                            )}
                        </>
                    )}
                </Formik>
                {snackbarMessage && (
                    <div className='snackbar-message success'>
                        {snackbarMessage}
                    </div>
                )}
            </motion.div>
        </>
    );
}


export default LoginForm;